import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, LogOut, ChevronLeft, Lock, Map as MapIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, RetellingBadge } from "@/components/cast/Common";
import { Emblem } from "@/components/cast/Emblem";
import { StoryPlayer } from "@/components/cast/StoryPlayer";
import { ListenAll } from "@/components/cast/ListenAll";
import { StickerBook } from "./KidsStickers";
import { BibleMap } from "@/components/cast/BibleMap";
import { FamilyMap } from "@/components/cast/FamilyMap";
import { KidsCatch } from "@/components/cast/KidsCatch";
import { CatchGarden } from "@/components/cast/CatchGarden";
import { VerseGiftBanner } from "@/components/cast/VerseGifts";
import { ChurchSeason } from "@/components/cast/ChurchSeason";
import { AdventCalendar } from "@/components/cast/AdventCalendar";
import { MemoryVerseGame } from "@/components/cast/MemoryVerse";

export function KidsMemory() {
  const { kid } = useAuth();
  return <KidsShell><MemoryVerseGame kidId={kid.id} /></KidsShell>;
}
import { EPISODE_PLACE, SERIES_PLACE } from "@/lib/bibleMap";

function KidsSeriesMap({ kidId, s }: { kidId: string; s: any }) {
  const key = `cast_harbour_kid_${kidId}_${s.id}`;
  const finished = s.episodes.length > 0 && s.completed_count === s.episodes.length;
  const [open, setOpen] = useState(() => finished && !localStorage.getItem(key));
  return (
    <div className="px-5 pb-6 sm:px-7">
      <button data-testid={`kids-map-toggle-${s.id}`} onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-full bg-[#F2CC8F]/40 px-5 py-2.5 text-lg font-semibold text-slate-800 transition-transform hover:scale-105">
        <MapIcon className="h-5 w-5 text-[#E07A5F]" />{open ? "Hide the map" : "See the map"}
      </button>
      {open && (
        <div className="mt-4">
          <BibleMap zoom testId={`kids-map-${s.id}`} celebrateKey={key} stops={s.episodes.map((e: any) => ({ key: e.id, place: EPISODE_PLACE[e.id] || SERIES_PLACE[s.id], label: `${e.order}. ${e.title}`, sub: e.ref, href: `/kids/episode/${e.id}`, done: e.completed, image: e.image }))} />
        </div>
      )}
    </div>
  );
}

function ExitKids() {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const check = async () => {
    try { await api.post("/parent/verify-pin", { pin }); localStorage.removeItem("cast_kid"); window.location.assign("/parent"); }
    catch (e) { setErr(errMsg(e)); setPin(""); }
  };
  return (
    <>
      <button data-testid="exit-kids-mode-button" onClick={() => setOpen(true)} className="flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm"><Lock className="h-4 w-4" />Grown-ups</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Parent PIN</DialogTitle><DialogDescription>Enter your 4-digit PIN to leave Kids Cove.</DialogDescription></DialogHeader>
          <Input data-testid="exit-kids-pin-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" placeholder="••••" className="text-center text-2xl tracking-[0.6em]" />
          {err && <p className="text-sm text-red-600" data-testid="exit-kids-error">{err}</p>}
          <Button data-testid="exit-kids-submit-button" disabled={pin.length !== 4} onClick={check} className="rounded-full bg-[#1E293B]"><LogOut className="mr-1 h-4 w-4" />Exit Kids Cove</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function KidsShell({ children, catchable }: { children: any; catchable?: boolean }) {
  const { kid } = useAuth();
  return (
    <div className="min-h-screen bg-[#FFFDF5] font-kids" data-testid="kids-mode">
      {catchable && <KidsCatch kidId={kid.id} />}
      <header className="sticky top-0 z-50 border-b-4 border-[#81B29A]/30 bg-[#FFFDF5]/90 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-6xl items-center justify-between px-5">
          <Link to="/kids" className="flex items-center gap-3" data-testid="kids-home-link"><Emblem avatar={kid.avatar} size={44} /><span className="hidden text-2xl font-semibold text-slate-800 sm:inline">Kids Cove</span></Link>
          <div className="flex items-center gap-2">
            <Link to="/kids/stickers" data-testid="kids-stickers-link" className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-amber-100 px-3.5 py-2 text-sm font-semibold text-amber-800 shadow-sm transition-transform hover:scale-105"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />Stickers</Link>
            <ExitKids />
          </div>
        </div>
      </header>
      <main className={`mx-auto max-w-6xl px-5 py-10 ${catchable ? "pb-28" : ""}`}>{children}</main>
    </div>
  );
}

export function KidsHome() {
  const { kid } = useAuth();
  const [series, setSeries] = useState<any[] | null>(null);
  useEffect(() => { api.get(`/kids/${kid.id}/series`).then((r) => setSeries(r.data)); }, [kid.id]);
  const stars = series?.reduce((n, s) => n + s.completed_count, 0) || 0;
  return (
    <KidsShell catchable>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="text-lg font-semibold text-[#E07A5F]">Hello, {kid.name}!</p><h1 className="mt-1 text-4xl font-semibold text-slate-800 sm:text-5xl">Pick a Bible adventure</h1></div>
        <div className="flex items-center gap-2 rounded-full bg-amber-100 px-5 py-2.5 text-xl font-semibold text-amber-800" data-testid="kids-star-count"><Star className="h-6 w-6 fill-amber-400 text-amber-400" />{stars} stars</div>
      </div>
      {series === null ? <Loader /> : (
        <div className="mt-10 space-y-10">
          <ChurchSeason kids />
          <VerseGiftBanner kidId={kid.id} />
          <AdventCalendar kidId={kid.id} />
          <Link to="/kids/memory" data-testid="kids-memory-link" className="flex items-center gap-4 rounded-[2rem] bg-[#FFF3D6] p-6 shadow ring-2 ring-amber-200 transition-transform hover:scale-[1.01]">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-300 text-3xl font-bold text-amber-900">?</span>
            <span><span className="block text-2xl font-semibold text-slate-800">Memory verse of the week</span><span className="text-lg text-slate-600">Drag the words into place, 3 levels!</span></span>
          </Link>
          <CatchGarden kidId={kid.id} />
          <FamilyMap kid={kid} />
          {series.map((s, i) => (
            <motion.section key={s.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} className="overflow-hidden rounded-3xl bg-white shadow-lg" data-testid={`kids-series-${s.id}`}>
              <div className="grid md:grid-cols-5">
                <img src={s.cover} alt={s.title} className="h-56 w-full object-cover md:col-span-2 md:h-full" />
                <div className="space-y-4 p-7 md:col-span-3">
                  <div className="flex flex-wrap items-center gap-3"><RetellingBadge refText={s.book} /><ListenAll kids testId={`kids-listen-all-${s.id}`} title={s.title} episodeIds={s.episodes.map((e: any) => e.id)} load={async (id) => (await api.get(`/kids/${kid.id}/episodes/${id}`)).data} /></div>
                  <h2 className="text-3xl font-semibold text-slate-800">{s.title}</h2>
                  <div className="grid gap-3">
                    {s.episodes.map((e: any) => (
                      <Link key={e.id} to={`/kids/episode/${e.id}`} data-testid={`kids-episode-${e.id}`} className="flex items-center gap-3 rounded-2xl bg-[#81B29A]/15 px-5 py-4 text-lg font-semibold text-slate-800 transition-transform hover:scale-[1.02]">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E07A5F] text-white">{e.order}</span>{e.title}
                        {e.completed && <Star className="ml-auto h-6 w-6 fill-amber-400 text-amber-400" />}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              <KidsSeriesMap kidId={kid.id} s={s} />
            </motion.section>
          ))}
        </div>
      )}
    </KidsShell>
  );
}

export function KidsStickers() {
  return <KidsShell catchable><StickerBook /></KidsShell>;
}

export function KidsEpisode() {
  const { kid } = useAuth();
  const { episodeId } = useParams();
  const [ep, setEp] = useState<any>(null);
  const [err, setErr] = useState("");
  useEffect(() => { setEp(null); api.get(`/kids/${kid.id}/episodes/${episodeId}`).then((r) => setEp(r.data)).catch((e) => setErr(errMsg(e))); api.post(`/kids/${kid.id}/story-visit`).catch(() => {}); }, [episodeId, kid.id]);
  return (
    <KidsShell>
      <Link to="/kids" data-testid="kids-back-link" className="mb-6 inline-flex items-center gap-1 text-lg font-semibold text-slate-600"><ChevronLeft className="h-5 w-5" />All adventures</Link>
      {err ? <p className="text-xl">{err}</p> : !ep ? <Loader /> : (
        <div className="mx-auto max-w-4xl space-y-6">
          <h1 className="text-4xl font-semibold text-slate-800" data-testid="kids-episode-title">{ep.title}</h1>
          <StoryPlayer kids episode={ep} base="/kids/episode/" onComplete={async (r) => (await api.post(`/kids/${kid.id}/progress`, { episode_id: ep.id, quiz_score: r.score, quiz_total: r.total, choices: r.choices })).data} />
        </div>
      )}
    </KidsShell>
  );
}
